import { useEffect, useState } from "react";
import { X, Trash2, ExternalLink } from "lucide-react";
import MessageCard from "./MessageCard";
import { toast } from "sonner";
import { getCliente, getMessaggi, deleteCliente, AREA_LABEL, OBIETTIVO_LABEL } from "./clubApi";
import { Btn, Card, Pill, ScoreRing, Bar } from "./ui";
import PlanEditor from "./PlanEditor";

const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

export default function ClientDrawer({ id, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = () => Promise.all([getCliente(id), getMessaggi(id)]).then(([x, m]) => { setC(x); setMsgs(m); }).catch(() => toast.error("Cliente non caricato"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const remove = async () => {
    if (!window.confirm(`Eliminare ${c.nome}? Non si può annullare.`)) return;
    await deleteCliente(id); toast.success("Eliminato"); onChanged(); onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="client-drawer">
      <div className="h-full w-full max-w-lg overflow-y-auto bg-[#0E0E10] border-l border-[#26262B] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-club-display text-2xl">{c?.nome || "…"}</p>
          <button onClick={onClose} className="text-[#9A948A] hover:text-white" data-testid="drawer-close"><X size={20} /></button>
        </div>
        {!c ? <div className="mt-10 w-6 h-6 border-2 border-[#E5B13A] border-t-transparent rounded-full animate-spin mx-auto" /> : (
          <>
            <div className="mt-4 flex items-center gap-4">
              <ScoreRing value={c.profilo.indice} size={104} label="partenza" testId="drawer-score" />
              <div className="text-sm">
                <Pill tone={{ nuovo: "gold", riordino: "red", checkin: "blue", percorso: "green" }[c.stato.key]}>{c.stato.label}</Pill>
                <p className="text-[#9A948A] mt-2">Test del {fmt(c.created_at)}</p>
                <p className="text-[#9A948A]">{c.whatsapp ? `WhatsApp ${c.whatsapp}` : "WhatsApp non lasciato"}</p>
                <div className="mt-2 flex flex-wrap gap-1">{(c.risposte.obiettivi || []).map((o) => <Pill key={o} tone="muted">{OBIETTIVO_LABEL[o] || o}</Pill>)}</div>
              </div>
            </div>
            {msgs && <a href={msgs.link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#F5D27A]" data-testid="drawer-client-link"><ExternalLink size={12} /> Apri la pagina del cliente</a>}

            <Card className="mt-5 space-y-2">
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A948A] mb-1">Le sue aree</p>
              {Object.entries(c.profilo.aree).map(([k, v]) => <Bar key={k} label={AREA_LABEL[k]} value={v} />)}
              <p className="text-xs text-[#9A948A] pt-2">Integrazione oggi: <b className="text-[#F4EFE6]">{{ no: "no", saltuario: "ogni tanto", si: "sì, costante" }[c.risposte.integrazione] || "—"}</b> · Età {c.risposte.eta || "—"}</p>
            </Card>

            <div className="mt-5">
              <div className="flex items-center justify-between">
                <p className="font-semibold">Percorso di {c.piano.durata_giorni || 30} giorni</p>
                {!editing && <Btn variant={c.piano.confermato ? "ghost" : "gold"} className="px-4 py-2 text-xs" onClick={() => setEditing(true)} data-testid="edit-plan-btn">{c.piano.confermato ? "Modifica" : "Rivedi e conferma"}</Btn>}
              </div>
              {editing ? (
                <PlanEditor cliente={c} onDone={() => { setEditing(false); load(); onChanged(); }} onCancel={() => setEditing(false)} />
              ) : (
                <Card className="mt-3 space-y-2" data-testid="plan-summary">
                  {!c.piano.confermato && <p className="text-xs text-[#F5D27A]">Proposta automatica dalle risposte. Confermala o cambiala prima di inviarla.</p>}
                  {c.piano.prodotti.map((p) => (
                    <div key={p.codice || p.nome} className="flex items-center justify-between text-sm border-b border-[#232328] last:border-0 pb-2 last:pb-0">
                      <div><p className="font-medium">{p.nome}</p><p className="text-xs text-[#9A948A]">{p.dose}</p></div>
                      <span className="text-xs text-[#9A948A] whitespace-nowrap">{p.fine_stimata ? `fine ${fmt(p.fine_stimata)}` : `${p.durata_giorni} gg`}</span>
                    </div>
                  ))}
                  {c.piano.note && <p className="text-sm italic text-[#F4EFE6]/80 pt-1">"{c.piano.note}"</p>}
                </Card>
              )}
            </div>

            {c.checkins?.length > 0 && (
              <Card className="mt-5 space-y-2" data-testid="drawer-checkins">
                <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A948A] mb-1">Check</p>
                {[...c.checkins].reverse().map((k) => (
                  <div key={k.data} className="text-sm border-b border-[#232328] last:border-0 pb-2 last:pb-0">
                    <div className="flex justify-between"><span className="text-[#9A948A]">{fmt(k.data)} · E{k.energia} S{k.sonno} C{k.costanza}</span><span className="font-club-display text-[#E5B13A]">{k.punteggio}</span></div>
                    {k.nota && <p className="text-xs text-[#F4EFE6]/80 mt-1">"{k.nota}"</p>}
                  </div>
                ))}
              </Card>
            )}

            {msgs && (
              <div className="mt-5">
                <p className="font-semibold">Messaggi pronti</p>
                <div className="mt-3 space-y-2">
                  {msgs.messaggi.map((m) => (
                    <MessageCard key={`${m.key}-${m.testo}`} m={m} warning={m.key === "piano" && !c.piano.confermato && <p className="mt-2 text-xs text-[#F5D27A]" data-testid="msg-piano-warning">Prima premi «Rivedi e conferma»: finché il percorso non è confermato il cliente non vede i prodotti.</p>} />
                  ))}
                </div>
              </div>
            )}

            <button onClick={remove} className="mt-8 flex items-center gap-1 text-xs text-[#6F6A62] hover:text-[#ff9a9e]" data-testid="delete-client-btn"><Trash2 size={13} /> Elimina cliente</button>
          </>
        )}
      </div>
    </div>
  );
}
